/* ──────────────────────────────────────────────────────────────
   Socks Up — Hero entrance + section-reveal animations
   Added 2026-06-03 overnight perf pass (Phase 3).

   Tech stack:
   - GSAP core (timeline orchestration)
   - GSAP ScrollTrigger (scroll-driven reveals)
   - Splitting.js (letter-by-letter heading split)
   - Lenis integration (via window.suLenis from Phase 2)

   Guard rails:
   - Respects prefers-reduced-motion (skipped entirely)
   - Hero copy is visible by default (CSS); GSAP only enhances
   - No FOUC: GSAP sets opacity AFTER it loads, briefly visible if CDN
     is slow — but never invisible (no .gsap class fallback)
   ────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Respect user preference
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function waitForDeps(cb) {
    if (typeof gsap === 'undefined' || typeof Splitting === 'undefined') {
      setTimeout(function () { waitForDeps(cb); }, 50);
      return;
    }
    if (gsap.plugins && !gsap.plugins.ScrollTrigger && typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    } else if (typeof ScrollTrigger !== 'undefined' && !gsap.core.globals().ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }
    cb();
  }

  function init() {
    waitForDeps(function () {

      // ─── Lenis ↔ ScrollTrigger bridge (Phase 2 + Phase 3 interop) ───
      if (window.suLenis && typeof ScrollTrigger !== 'undefined') {
        window.suLenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add(function (time) { window.suLenis.raf(time * 1000); });
        gsap.ticker.lagSmoothing(0);
      }

      // ─── HERO ENTRANCE TIMELINE ────────────────────────────────────
      var hero = document.querySelector('.su-hero');
      if (hero) {
        var h1 = hero.querySelector('.su-hero__h1');

        // Detect Arabic / RTL — in Arabic, letters CONNECT cursively.
        // Splitting.js wraps each char in <span style="display:inline-block">,
        // which BREAKS those cursive joins (every letter renders as standalone
        // disconnected glyph). For Arabic we use a whole-line fade-up instead.
        var docEl = document.documentElement;
        var isArabic =
          (docEl.lang || '').toLowerCase().indexOf('ar') === 0 ||
          (docEl.dir || '').toLowerCase() === 'rtl' ||
          (hero && hero.closest('[dir="rtl"]') !== null);

        // Only split the H1 into characters for LTR (English etc).
        var doCharSplit = !isArabic && h1 && !h1.classList.contains('splitting');
        if (doCharSplit) {
          Splitting({ target: h1, by: 'chars' });
        }

        // Initial state — instantly hide things GSAP will animate in
        gsap.set('.su-hero__badge, .su-hero__sub, .su-hero__ctas .su-btn, .su-hero-stat', { autoAlpha: 0, y: 12 });
        if (doCharSplit) {
          gsap.set(h1.querySelectorAll('.char'), { autoAlpha: 0, y: 20, rotateX: -45 });
        } else if (h1) {
          // RTL/Arabic path: animate the whole heading as one block (no per-char split)
          gsap.set(h1, { autoAlpha: 0, y: 20 });
        }
        gsap.set('.su-hero__circle-1, .su-hero__circle-2', { scale: 0.6, autoAlpha: 0, transformOrigin: 'center' });
        gsap.set('.su-hero__img-wrap', { autoAlpha: 0, y: 30 });
        gsap.set('.su-hero__price-badge', { autoAlpha: 0, scale: 0.8 });

        var tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.7 } });
        tl
          .to('.su-hero__badge', { autoAlpha: 1, y: 0 }, 0.05)
          .to('.su-hero__circle-1, .su-hero__circle-2', { autoAlpha: 1, scale: 1, stagger: 0.08, duration: 0.9, ease: 'expo.out' }, 0.1);

        if (doCharSplit) {
          tl.to(h1.querySelectorAll('.char'), { autoAlpha: 1, y: 0, rotateX: 0, stagger: 0.018, duration: 0.55 }, 0.2);
        } else if (h1) {
          tl.to(h1, { autoAlpha: 1, y: 0, duration: 0.75, ease: 'power2.out' }, 0.2);
        }

        tl
          .to('.su-hero__sub', { autoAlpha: 1, y: 0 }, 0.55)
          .to('.su-hero__img-wrap', { autoAlpha: 1, y: 0, duration: 0.85, ease: 'expo.out' }, 0.4)
          .to('.su-hero__price-badge', { autoAlpha: 1, scale: 1, duration: 0.5, ease: 'back.out(1.6)' }, 0.85)
          .to('.su-hero__ctas .su-btn', { autoAlpha: 1, y: 0, stagger: 0.08, duration: 0.5 }, 0.7)
          .to('.su-hero-stat', { autoAlpha: 1, y: 0, stagger: 0.07, duration: 0.5, onComplete: countUpStats }, 0.85);

        // Idle float on the hero product image — starts after entrance settles.
        tl.call(function () {
          gsap.to('.su-hero__img-wrap', { y: -8, duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1 });
        }, null, 1.6);
      }

      // ─── STAT COUNT-UP (2026-06-11) ───────────────────────────────
      // "100k+" → counts 0→100 keeping the suffix; "4.9" → 0→4.9.
      // Non-numeric-leading stats (e.g. "1–3") are left untouched.
      function countUpStats() {
        document.querySelectorAll('.su-hero-stat__n').forEach(function (el) {
          var m = (el.textContent || '').trim().match(/^([\d.]+)(.*)$/);
          if (!m) return;
          var target = parseFloat(m[1]);
          if (!isFinite(target)) return;
          var suffix = m[2] || '';
          var decimals = (m[1].split('.')[1] || '').length;
          var obj = { v: 0 };
          gsap.to(obj, {
            v: target, duration: 1.4, ease: 'power2.out',
            onUpdate: function () { el.textContent = obj.v.toFixed(decimals) + suffix; },
            onComplete: function () { el.textContent = m[1] + suffix; }
          });
        });
      }

      // ─── SECTION REVEALS ON SCROLL ─────────────────────────────────
      // Eyebrow + heading fade-up when section enters viewport.
      var sectionSelectors = [
        '.su-bestsellers', '.su-cats', '.su-bundle', '.su-bundle-teaser',
        '.su-reviews', '.su-loyalty', '.su-faq', '.su-ugc',
      ];
      sectionSelectors.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (section) {
          var eyebrow = section.querySelector('.su-mono, [class*="__eyebrow"]');
          var heading = section.querySelector('.su-serif, [class*="__heading"], h2');
          if (!eyebrow && !heading) return;
          var targets = [];
          if (eyebrow) targets.push(eyebrow);
          if (heading) targets.push(heading);
          gsap.fromTo(targets,
            { autoAlpha: 0, y: 24 },
            {
              autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.08,
              scrollTrigger: { trigger: section, start: 'top 80%', toggleActions: 'play none none none' }
            }
          );
        });
      });

      // Refresh ScrollTrigger once images/fonts load (sizes can change)
      window.addEventListener('load', function () { if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh(); });
    });
  }

  ready(init);
})();
